import { useState, useEffect } from 'react';
import { Shield, Copy, Check, AlertTriangle, Smartphone, Heart } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ScudoTab = ({ famiglia }) => {
  const [copied, setCopied] = useState(false);
  const [alertHistory, setAlertHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Genera il link Scudo basato sull'ID famiglia
  const scudoLink = famiglia?.id ? `${window.location.origin}/scudo/${famiglia.id}` : '';
  
  // Carica lo storico degli alert
  useEffect(() => {
    const fetchAlertHistory = async () => {
      if (!famiglia?.id) return;
      
      try {
        const response = await fetch(`${API_URL}/api/scudo/history/${famiglia.id}`);
        if (response.ok) {
          const data = await response.json();
          setAlertHistory(data.alerts || []);
        }
      } catch (error) {
        console.error('Errore caricamento storico alert:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchAlertHistory();
  }, [famiglia?.id]);
  
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(scudoLink);
      setCopied(true);
      toast.success('Link copiato! Invialo al tuo assistito');
      setTimeout(() => setCopied(false), 3000);
    } catch (error) {
      // Fallback: seleziona il testo nel campo per copia manuale
      const textField = document.querySelector('[data-scudo-link]');
      if (textField) {
        // Crea un input temporaneo per selezionare il testo
        const tempInput = document.createElement('input');
        tempInput.value = scudoLink;
        document.body.appendChild(tempInput);
        tempInput.select();
        tempInput.setSelectionRange(0, 99999); // Per mobile
        
        try {
          document.execCommand('copy');
          setCopied(true);
          toast.success('Link copiato! Invialo al tuo assistito');
          setTimeout(() => setCopied(false), 3000);
        } catch (e) {
          toast.info('Seleziona e copia il link manualmente');
        }
        
        document.body.removeChild(tempInput);
      } else {
        toast.info('Seleziona e copia il link manualmente');
      }
    }
  };
  
  const shareLink = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'CuraCheck Scudo - Emergenza',
          text: `Apri questo link in caso di emergenza. Clicca 3 volte sul pulsante per avvisarmi.`,
          url: scudoLink
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          copyLink();
        }
      }
    } else {
      copyLink();
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-6 border border-red-100">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-14 h-14 bg-red-500 rounded-2xl flex items-center justify-center shadow-lg">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Tasto Scudo</h2>
            <p className="text-red-600 font-medium">Sistema di emergenza per {famiglia?.nome_assistito}</p>
          </div>
        </div>
        
        <p className="text-gray-600 leading-relaxed">
          Invia questo link speciale al tuo assistito. In caso di emergenza, potrà cliccare 
          <strong className="text-red-600"> 3 volte </strong> 
          sul pulsante per inviarti una notifica di emergenza immediata.
        </p>
      </div>
      
      {/* Link da condividere */}
      <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Smartphone className="w-5 h-5 text-gray-500" />
          Link per l'assistito
        </h3>
        
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 font-mono text-sm text-gray-600 truncate select-all cursor-text" data-scudo-link="true" onClick={(e) => e.target.select && window.getSelection().selectAllChildren(e.target)}>
            {scudoLink || 'Caricamento...'}
          </div>
          
          <div className="flex gap-2">
            <Button
              onClick={copyLink}
              variant="outline"
              className="flex items-center gap-2 border-gray-300"
            >
              {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copiato!' : 'Copia'}
            </Button>
            
            <Button
              onClick={shareLink}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              Condividi
            </Button>
          </div>
        </div>
        
        <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <div className="flex gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <strong>Importante:</strong> Salva questo link nei preferiti del telefono dell'assistito 
              o creane un collegamento sulla schermata Home per un accesso rapido in caso di emergenza.
            </div>
          </div>
        </div>
      </div>
      
      {/* Come funziona */}
      <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
        <h3 className="font-semibold text-gray-900 mb-4">Come funziona</h3>
        
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="text-center p-4 bg-gray-50 rounded-xl">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-blue-600 font-bold">1</span>
            </div>
            <p className="text-sm text-gray-600">L'assistito apre il link sul suo telefono</p>
          </div>
          
          <div className="text-center p-4 bg-gray-50 rounded-xl">
            <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-orange-600 font-bold">2</span>
            </div>
            <p className="text-sm text-gray-600">Clicca 3 volte sul grande pulsante rosso</p>
          </div>
          
          <div className="text-center p-4 bg-gray-50 rounded-xl">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-red-600 font-bold">3</span>
            </div>
            <p className="text-sm text-gray-600">Tu ricevi subito una notifica di emergenza</p>
          </div>
        </div>
      </div>
      
      {/* Storico Alert */}
      <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Heart className="w-5 h-5 text-red-500" />
          Storico emergenze
        </h3>
        
        {loading ? (
          <div className="text-center py-8 text-gray-500">Caricamento...</div>
        ) : alertHistory.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Shield className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p>Nessuna emergenza registrata</p>
            <p className="text-sm text-gray-400 mt-1">È una buona notizia!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {alertHistory.slice(0, 10).map((alert, index) => (
              <div 
                key={alert.id || index}
                className="flex items-center justify-between p-4 bg-red-50 border border-red-100 rounded-xl"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Emergenza attivata</p>
                    <p className="text-sm text-gray-500">
                      {new Date(alert.created_at).toLocaleString('it-IT', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ScudoTab;
